// Backstop against Hosted UI redirect loops: every individual step (guard redirect, Cognito
// re-authorizing, a failing exchange) reports success on its own, so only a counter that survives
// the navigation can catch it. sessionStorage, not memory, since each iteration is a full page
// load; per-tab, so one tab looping does not lock out another.
const KEY = 'auth:redirect-attempts';

/** Two consecutive redirects is normal; the third in quick succession is a loop. */
const MAX_ATTEMPTS = 3;

// Attempts older than this are forgotten: without a window the counter only climbs, so ordinary
// spread-out behavior (browser-back from Hosted UI, retrying a login later) would eventually trip
// the breaker and lock out a user who never looped. A real loop completes in seconds, so a short
// window separates the two.
const WINDOW_MS = 60_000;

type Attempts = { count: number; firstAt: number };

function read(): Attempts {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return { count: 0, firstAt: 0 };
    const parsed = JSON.parse(raw) as Partial<Attempts>;
    const count = Number(parsed.count) || 0;
    const firstAt = Number(parsed.firstAt) || 0;
    if (Date.now() - firstAt > WINDOW_MS) return { count: 0, firstAt: 0 };
    return { count, firstAt };
  } catch {
    // Storage disabled or corrupt value. Degrade to "no loop detected" rather than blocking
    // login outright — a broken counter must not stop people signing in.
    return { count: 0, firstAt: 0 };
  }
}

/** Records an outgoing Hosted UI redirect. Returns false when this attempt exceeds the cap. */
export function noteRedirectAttempt(): boolean {
  const { count, firstAt } = read();
  const next: Attempts = {
    count: count + 1,
    firstAt: count === 0 ? Date.now() : firstAt,
  };
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    return true;
  }
  return next.count <= MAX_ATTEMPTS;
}

/**
 * Called once a session is genuinely established, and from the post-logout page. The signed-out
 * reset matters as much as the success one: it is the only screen a locked-out anonymous user can
 * still reach, so without it the breaker has no in-app recovery and only closing the tab clears it.
 */
export function resetRedirectAttempts(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}

export function hasExceededRedirects(): boolean {
  return read().count > MAX_ATTEMPTS;
}

// One-shot proof that this tab has just sent the browser to Cognito's /logout, so the hosted cookie
// is being cleared and `/auth/signed-out` may continue straight to sign-in. Invariant: written only
// immediately before that assignment. Written anywhere else (a sibling tab's broadcast, a failed
// refresh, the mock or uninitialized logout branches), auto-continue would silently sign a user
// back in through a cookie that is still live. Per-tab sessionStorage, which survives the same-tab
// /logout round trip.
const SIGNED_OUT_KEY = 'auth:signed-out-via-cognito';

// Long enough for the /logout round trip, short enough that pressing back into the page later (or
// after a /logout that errored) gets the manual screen.
const SIGNED_OUT_FRESH_MS = 30_000;

// A marker written by this very document proves nothing yet: /logout has not navigated away. An
// in-app visit to /auth/signed-out in that window (a refresh the revoke just killed) must not start
// a sign-in, whose navigation could override the pending /logout while the cookie is still live.
let markedInThisDocument = false;

/** Where `/auth/signed-out` continues once the /logout round trip has completed. */
export type SignOutContinuation = 'login' | 'forgot-password';

export function markCognitoSignOut(next: SignOutContinuation = 'login'): void {
  markedInThisDocument = true;
  try {
    // The continuation rides in the same one-shot value, so it is consumed with the marker and can
    // never outlive it to redirect a later, ordinary sign-out.
    const value = next === 'login' ? String(Date.now()) : `${Date.now()}:${next}`;
    window.sessionStorage.setItem(SIGNED_OUT_KEY, value);
  } catch {
    // Storage unavailable: the post-logout page stays manual, which is the safe default.
  }
}

/**
 * Reads and clears the marker. Returns where to continue only when it is fresh and was written by
 * an earlier document, i.e. the /logout round trip has completed; anything unreadable is null.
 */
export function takeCognitoSignOut(): SignOutContinuation | null {
  // Left in storage: the document that /logout lands on still needs it.
  if (markedInThisDocument) return null;
  try {
    const raw = window.sessionStorage.getItem(SIGNED_OUT_KEY);
    window.sessionStorage.removeItem(SIGNED_OUT_KEY);
    if (raw === null) return null;
    const [at, next] = raw.split(':');
    const age = Date.now() - Number(at);
    if (!(age >= 0 && age < SIGNED_OUT_FRESH_MS)) return null;
    return next === 'forgot-password' ? 'forgot-password' : 'login';
  } catch {
    return null;
  }
}
